/**
 * React Query hooks for Fulfillment APIs.
 *
 * Query keys include tenant to prevent cross-tenant cache collisions.
 * Uses keepPreviousData for smooth transitions per global engineering practices.
 *
 * Plan: 123-pick-pack-fulfillment, Epic 01 + 04
 */

import { useCallback } from "react";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { erp } from "@/lib/erp";
import { fulfillmentApi } from "./fulfillmentApi";
import type {
  CreateSessionResult,
  UpdatePickStepResult,
  CreateDeliveryResult,
  PrintUrlResult,
  EnsureLabelResult,
  SOPrintUrlResult,
  SetTrackingResult,
  RecordShipmentResult,
  PauseSessionResult,
  ResumeSessionResult,
  PartialShipmentSettings,
} from "./fulfillmentApi";

function useTenant() {
  return useAuthStore((s) => s.currentTenant);
}

export const fulfillmentKeys = {
  all: (tenant: string | null) => ["fulfillment", tenant] as const,
  pendingOrders: (tenant: string | null) =>
    ["fulfillment", "pending-orders", tenant] as const,
  batchSuggestions: (tenant: string | null) =>
    ["fulfillment", "batch-suggestions", tenant] as const,
  pickSteps: (sessionId: string, tenant: string | null, expandSets = false) =>
    ["fulfillment", "pick-steps", sessionId, tenant, expandSets] as const,
  sortInstructions: (sessionId: string, tenant: string | null) =>
    ["fulfillment", "sort-instructions", sessionId, tenant] as const,
  packOrders: (sessionId: string, tenant: string | null) =>
    ["fulfillment", "pack-orders", sessionId, tenant] as const,
  activeSessions: (tenant: string | null) =>
    ["fulfillment", "active-sessions", tenant] as const,
  personalizationQueue: (params: string, tenant: string | null) =>
    ["fulfillment", "personalization-queue", params, tenant] as const,
  pwoDetail: (pwoName: string, tenant: string | null) =>
    ["fulfillment", "pwo-detail", pwoName, tenant] as const,
  orderReadiness: (salesOrder: string, tenant: string | null) =>
    ["fulfillment", "order-readiness", salesOrder, tenant] as const,
  shipmentHistory: (salesOrder: string, tenant: string | null) =>
    ["fulfillment", "shipment-history", salesOrder, tenant] as const,
  partialShipmentSettings: (tenant: string | null) =>
    ["fulfillment", "partial-shipment-settings", tenant] as const,
  dashboardMetrics: (dateRange: string, tenant: string | null) =>
    ["fulfillment", "dashboard-metrics", dateRange, tenant] as const,
  workerPerformance: (dateRange: string, tenant: string | null) =>
    ["fulfillment", "worker-performance", dateRange, tenant] as const,
  fulfillmentAlerts: (tenant: string | null) =>
    ["fulfillment", "fulfillment-alerts", tenant] as const,
  sessionSummary: (sessionId: string, tenant: string | null) =>
    ["fulfillment", "session-summary", sessionId, tenant] as const,
  fulfillmentCounts: (tenant: string | null) =>
    ["fulfillment", "counts", tenant] as const,
  pendingOrdersPaginated: (params: string, tenant: string | null) =>
    ["fulfillment", "pending-orders-paginated", params, tenant] as const,
  completedSessions: (tenant: string | null) =>
    ["fulfillment", "completed-sessions", tenant] as const,
  parkedOrders: (tenant: string | null) =>
    ["fulfillment", "parked-orders", tenant] as const,
};

/**
 * Invalidate the pack-and-ship session lists + count badge so cancelled /
 * resumed sessions disappear or refresh immediately.
 *
 * We invalidate the EXACT list keys rather than `fulfillmentKeys.all(tenant)`.
 * `all(tenant)` returns `["fulfillment", tenant]`, but every live query key is
 * `["fulfillment", "<descriptor>", tenant]` — the tenant sits AFTER the
 * descriptor, so `["fulfillment", tenant]` is not a prefix of any live key and
 * React Query's partial match silently invalidates nothing. That is the root
 * cause of the reported bug: after a successful cancel the sessions list was
 * never refetched, so the cancelled card stayed on screen.
 */
function invalidateSessionLists(queryClient: QueryClient, tenant: string | null) {
  queryClient.invalidateQueries({ queryKey: fulfillmentKeys.activeSessions(tenant) });
  queryClient.invalidateQueries({ queryKey: fulfillmentKeys.completedSessions(tenant) });
  queryClient.invalidateQueries({ queryKey: fulfillmentKeys.fulfillmentCounts(tenant) });
}

/**
 * Backend validation message (pick_api._validate_session) raised as a 417 when
 * a session is already in a terminal state. Re-cancelling an already-cancelled
 * session is benign, so we treat this as "already gone" instead of an error.
 */
const ALREADY_TERMINAL_RE = /Session is in '(Cancelled|Completed)' status/i;

export function useFulfillmentCounts() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.fulfillmentCounts(tenant),
    queryFn: () => fulfillmentApi.getFulfillmentCounts(),
    enabled: !!tenant,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useParkedOrders() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.parkedOrders(tenant),
    queryFn: () => fulfillmentApi.getParkedPersonalizationOrders(),
    enabled: !!tenant,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePendingOrdersPaginated(params: {
  search: string;
  priorityFilter: string;
  readinessFilter: string;
  channelFilter: string;
  /** JSON list / CSV of storefront keys to restrict to (store filter). */
  storefrontSources?: string;
  /** Plan 1954 — ISO date; only orders ready/packed on/after this date. */
  dateFrom?: string;
  enabled?: boolean;
}) {
  const tenant = useTenant();
  const storefrontSources = params.storefrontSources ?? "";
  const dateFrom = params.dateFrom ?? "";
  const stableKey = JSON.stringify({
    search: params.search,
    priorityFilter: params.priorityFilter,
    readinessFilter: params.readinessFilter,
    channelFilter: params.channelFilter,
    storefrontSources,
    dateFrom,
  });

  return useInfiniteQuery({
    queryKey: fulfillmentKeys.pendingOrdersPaginated(stableKey, tenant),
    queryFn: ({ pageParam = 1 }) =>
      fulfillmentApi.getPendingOrdersPaginated({
        page: pageParam,
        page_size: 50,
        search: params.search,
        priority_filter: params.priorityFilter,
        readiness_filter: params.readinessFilter,
        channel_filter: params.channelFilter,
        ...(storefrontSources && { storefront_sources: storefrontSources }),
        ...(dateFrom && { date_from: dateFrom }),
      }),
    enabled: !!tenant && (params.enabled !== false),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.page_size;
      return loaded < lastPage.total_count ? lastPage.page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePendingOrders() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.pendingOrders(tenant),
    queryFn: () => fulfillmentApi.getPendingOrders(),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function useBatchSuggestions(maxBatchSize = 10) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.batchSuggestions(tenant),
    queryFn: () => fulfillmentApi.suggestBatches(maxBatchSize),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function useCreateFulfillmentSession() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<
    CreateSessionResult,
    Error,
    { salesOrders: string[]; readyItemsOnly?: boolean }
  >({
    mutationFn: ({ salesOrders, readyItemsOnly }) =>
      fulfillmentApi.createSession(salesOrders, readyItemsOnly),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.all(tenant),
      });
      if (data.skipped_orders?.length) {
        toast.warning(
          `ข้ามออเดอร์ที่ไม่มีสินค้าให้หยิบ: ${data.skipped_orders.join(", ")}`,
          {
            description:
              "สต๊อกหมดหรือข้อมูลตัวเลือกสินค้าไม่ครบ — ออเดอร์ยังอยู่ในรายการรอจัดส่ง",
            duration: 10000,
          },
        );
      }
      toast.success("Fulfillment session created");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create session");
    },
  });
}

// ── Epic 02: Pick Walk hooks ──────────────────────────────

export function usePickSteps(sessionId: string, expandSets = false) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.pickSteps(sessionId, tenant, expandSets),
    queryFn: () => fulfillmentApi.getPickSteps(sessionId, expandSets),
    enabled: !!tenant && !!sessionId,
    placeholderData: keepPreviousData,
  });
}

interface UpdatePickStepVars {
  pickListItem: string;
  action: "confirm" | "short" | "damage";
  params?: { qty_picked?: number; reason?: string; damage_notes?: string };
}

export function useUpdatePickStep(sessionId: string) {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<UpdatePickStepResult, Error, UpdatePickStepVars>({
    mutationFn: ({ pickListItem, action, params }) =>
      fulfillmentApi.updatePickStep(sessionId, pickListItem, action, params),
    onSuccess: () => {
      // Prefix match (no expandSets) invalidates BOTH the walk and list variants.
      queryClient.invalidateQueries({
        queryKey: ["fulfillment", "pick-steps", sessionId, tenant],
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update pick step");
    },
  });
}

export function useUploadDamagePhoto(sessionId: string) {
  return useMutation<{ file_url: string }, Error, { pickListItem: string; file: File }>({
    mutationFn: ({ pickListItem, file }) =>
      fulfillmentApi.uploadDamagePhoto(sessionId, pickListItem, file),
    onError: (error) => {
      toast.error(error.message || "Failed to upload photo");
    },
  });
}

export function useVerifyBarcode() {
  return useMutation<
    { match: boolean; expected_barcodes: string[] },
    Error,
    { itemCode: string; scannedBarcode: string }
  >({
    mutationFn: ({ itemCode, scannedBarcode }) =>
      fulfillmentApi.verifyBarcode(itemCode, scannedBarcode),
  });
}

// ── Epic 03: Sort + Pack hooks ──────────────────────────

export function useSortInstructions(sessionId: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.sortInstructions(sessionId, tenant),
    queryFn: () => fulfillmentApi.getSortInstructions(sessionId),
    enabled: !!tenant && !!sessionId,
    placeholderData: keepPreviousData,
  });
}

export function useCompleteSort(sessionId: string) {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, void>({
    mutationFn: () => fulfillmentApi.completeSort(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.sortInstructions(sessionId, tenant),
      });
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.packOrders(sessionId, tenant),
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to complete sorting");
    },
  });
}

export function usePackOrders(sessionId: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.packOrders(sessionId, tenant),
    queryFn: () => fulfillmentApi.getPackOrders(sessionId),
    enabled: !!tenant && !!sessionId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Returns a stable callback that invalidates the pack-orders query for one
 * session (exact tenant-scoped key — see the invalidateSessionLists note on why
 * `fulfillmentKeys.all()` is a silent no-op). Used by the pack station after a
 * marketplace ship so the tracking chip / status flips without a manual reload.
 */
export function useInvalidatePackOrders(sessionId: string) {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: fulfillmentKeys.packOrders(sessionId, tenant),
    });
  }, [queryClient, sessionId, tenant]);
}

/**
 * Returns a stable callback that invalidates the pack-and-ship session lists +
 * count badge (active + completed). Used by the pack-queue batch-ship flow so a
 * session whose Shopee orders just shipped refreshes its status/readiness (and
 * drops the batch-ship affordance) without a manual reload. Wraps the same exact
 * key invalidation the cancel/resume mutations use — never the `all()` no-op.
 */
export function useInvalidateSessionLists() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useCallback(() => {
    invalidateSessionLists(queryClient, tenant);
  }, [queryClient, tenant]);
}

export function useSessionSummary(sessionId: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.sessionSummary(sessionId, tenant),
    queryFn: () => fulfillmentApi.getSessionSummary(sessionId),
    enabled: !!tenant && !!sessionId,
    placeholderData: keepPreviousData,
  });
}

export function useCreateDeliveryAndPack(sessionId: string) {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<CreateDeliveryResult, Error, string>({
    mutationFn: (salesOrder) =>
      fulfillmentApi.createDeliveryAndPack(sessionId, salesOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.packOrders(sessionId, tenant),
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create delivery note");
    },
  });
}

export function useUploadPackPhoto(sessionId: string) {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    { file_url: string },
    Error,
    { salesOrder: string; file: File }
  >({
    mutationFn: ({ salesOrder, file }) =>
      fulfillmentApi.uploadPackPhoto(sessionId, salesOrder, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.packOrders(sessionId, tenant),
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to upload photo");
    },
  });
}

export function useCompletePackOrder(sessionId: string) {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; all_complete: boolean },
    Error,
    string
  >({
    mutationFn: (salesOrder) =>
      fulfillmentApi.completePackOrder(sessionId, salesOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.packOrders(sessionId, tenant),
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to complete order");
    },
  });
}

export function useGetPrintUrl() {
  return useMutation<PrintUrlResult, Error, string>({
    mutationFn: (deliveryNote) => fulfillmentApi.getPrintUrl(deliveryNote),
    onError: (error) => {
      toast.error(error.message || "Failed to get print URL");
    },
  });
}

/**
 * Ensure a Delivery Note exists for an order (creating a draft if needed) and
 * return its shipping-label URL — powers the direct "Print Shipping Label"
 * action on the completed-PWO drawer (TKT-2026-00084). Idempotent server-side
 * (reuses any open DN). On success, refresh the order's readiness + shipment
 * history so the drawer reflects the new DN.
 */
export function useEnsureDeliveryLabel() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<EnsureLabelResult, Error, string>({
    mutationFn: (salesOrder) => fulfillmentApi.ensureDeliveryLabel(salesOrder),
    onSuccess: (_result, salesOrder) => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.shipmentHistory(salesOrder, tenant),
      });
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.orderReadiness(salesOrder, tenant),
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to prepare shipping label");
    },
  });
}

export function useGetSOPrintUrl() {
  return useMutation<SOPrintUrlResult, Error, string>({
    mutationFn: (salesOrder) => fulfillmentApi.getSOPrintUrl(salesOrder),
    onError: (error) => {
      toast.error(error.message || "Failed to get print URL");
    },
  });
}

export function useSetTrackingNumber() {
  return useMutation<
    SetTrackingResult,
    Error,
    { deliveryNote: string; trackingNo: string; transporterName?: string }
  >({
    mutationFn: ({ deliveryNote, trackingNo, transporterName }) =>
      fulfillmentApi.setTrackingNumber(deliveryNote, trackingNo, transporterName),
    onError: (error) => {
      toast.error(error.message || "Failed to save tracking number");
    },
  });
}

/**
 * Plan 1954 — record a shipment from the Awaiting-Shipment queue.
 * Ensures a (draft) Delivery Note, sets tracking, and (unless notify=false)
 * fires the customer LINE. On success the shipped order drops out of the
 * queue, so we invalidate every pending-orders-paginated query + the counts.
 */
export function useShipFromQueue() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    RecordShipmentResult,
    Error,
    { salesOrder: string; trackingNo: string; transporterName?: string; notify?: boolean }
  >({
    mutationFn: ({ salesOrder, trackingNo, transporterName, notify = true }) =>
      fulfillmentApi.recordShipment(salesOrder, trackingNo, transporterName, notify),
    onSuccess: () => {
      // pendingOrdersPaginated keys vary by params — invalidate by descriptor
      // (queryKey[1]) rather than the .all() prefix, which is a no-op here.
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "fulfillment" &&
          q.queryKey[1] === "pending-orders-paginated",
      });
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.fulfillmentCounts(tenant),
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to record shipment");
    },
  });
}

// ── Epic 04: Session Management hooks ──────────────────────

export function useActiveSessions() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.activeSessions(tenant),
    queryFn: () => fulfillmentApi.getActiveSessions(),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function useCompletedSessions() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.completedSessions(tenant),
    queryFn: () => fulfillmentApi.getCompletedSessions(),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function usePauseSession() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<
    PauseSessionResult,
    Error,
    { sessionId: string; currentStep?: number }
  >({
    mutationFn: ({ sessionId, currentStep }) =>
      fulfillmentApi.pauseSession(sessionId, currentStep),
    onSuccess: () => {
      // Invalidate broadly — session lists and order pools may change
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.all(tenant),
      });
      toast.success("Session paused");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to pause session");
    },
  });
}

export function useResumeSession() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<ResumeSessionResult, Error, string>({
    mutationFn: (sessionName) => fulfillmentApi.resumeSession(sessionName),
    onSuccess: (_result, sessionName) => {
      // Exact-key invalidation. `fulfillmentKeys.all(tenant)` is a silent no-op
      // (tenant sits AFTER the descriptor, so it prefix-matches nothing — see
      // invalidateSessionLists docs). Refresh the session lists AND the resumed
      // session's pack-orders so the detail page reflects "Packing", not the
      // stale "Pack Paused".
      invalidateSessionLists(queryClient, tenant);
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.packOrders(sessionName, tenant),
      });
      toast.success("Session resumed");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to resume session");
    },
  });
}

export function useCancelSession() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (sessionName) => fulfillmentApi.cancelSession(sessionName),
    onSuccess: () => {
      // FIX (Part A): refetch the session lists so the cancelled card is removed.
      invalidateSessionLists(queryClient, tenant);
      toast.success("Session cancelled");
    },
    onError: (error) => {
      // FIX (Part B): re-cancelling an already-cancelled/completed session is
      // benign — the backend rightly rejects it with a 417. The session is
      // already gone, so refresh the lists (drops the stale card) instead of
      // surfacing the raw backend traceback. Genuine errors still toast.
      if (ALREADY_TERMINAL_RE.test(error?.message ?? "")) {
        invalidateSessionLists(queryClient, tenant);
        toast.info("Session already cancelled");
        return;
      }
      toast.error(error.message || "Failed to cancel session");
    },
  });
}

// ── Epic 05: Personalization Work Order hooks ──────────────

/**
 * Personalization Kanban queue — server-side search + newest-first pagination.
 *
 * The Queued column can hold thousands of PWOs (CSIDE: 3,036), so it is
 * paginated via useInfiniteQuery. `search` is sent to the backend so an SO
 * number is found across the WHOLE backlog, not just the loaded rows. Query key
 * includes a stable JSON of {assignedTo, search} so each search caches
 * independently (rule: stable query keys via JSON.stringify).
 */
export function usePersonalizationQueue(params?: {
  assignedTo?: string;
  search?: string;
}) {
  const tenant = useTenant();
  const assignedTo = params?.assignedTo;
  const search = params?.search ?? "";
  const stableKey = JSON.stringify({ assignedTo: assignedTo ?? "", search });

  return useInfiniteQuery({
    queryKey: fulfillmentKeys.personalizationQueue(stableKey, tenant),
    queryFn: ({ pageParam = 1 }) =>
      fulfillmentApi.getPersonalizationQueue({
        page: pageParam,
        page_size: 50,
        search,
        assignedTo,
      }),
    enabled: !!tenant,
    initialPageParam: 1,
    // Only the Queued column is paginated. Drive "load more" off the backend's
    // authoritative `queued_has_more` flag (not a total-vs-loaded arithmetic),
    // because the paid-gate can drop rows from a page so queued_total is only an
    // upper bound. Return the next page number while more remain, else undefined.
    getNextPageParam: (lastPage) =>
      lastPage.pagination.queued_has_more
        ? lastPage.pagination.page + 1
        : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Invalidate every personalization-queue cache entry for the current tenant
 * (all search / assignedTo variants). We use a tenant-scoped predicate — NOT
 * `fulfillmentKeys.all(tenant)` (a silent no-op, see invalidateSessionLists) —
 * because the descriptor sits before the params+tenant segments in the key.
 */
function invalidatePersonalizationQueue(
  queryClient: QueryClient,
  tenant: string | null,
) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "fulfillment" &&
      query.queryKey[1] === "personalization-queue" &&
      query.queryKey[query.queryKey.length - 1] === tenant,
  });
}

export function usePWODetail(pwoName: string | null) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.pwoDetail(pwoName || "", tenant),
    queryFn: () => fulfillmentApi.getPWODetail(pwoName!),
    enabled: !!tenant && !!pwoName,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch the Sales Order doc behind a PWO so the Complete drawer can surface the
 * shipping destination and route staff to Pack & Ship. Reuses the shared
 * `erp.orders.detail` adapter (full-doc frappeGet) — NO new backend endpoint —
 * and deliberately shares the `["orders", "detail", name]` cache key with the
 * orders feature's `useOrder`, so both consumers hit the same cached doc.
 */
export function usePWOSalesOrder(salesOrder: string | null) {
  return useQuery({
    queryKey: ["orders", "detail", salesOrder || ""],
    queryFn: () => erp.orders.detail(salesOrder!),
    enabled: !!salesOrder,
    placeholderData: keepPreviousData,
  });
}

export function useUpdatePWOStatus() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; status: string },
    Error,
    { pwoName: string; newStatus: string; notes?: string }
  >({
    mutationFn: ({ pwoName, newStatus, notes }) =>
      fulfillmentApi.updatePWOStatus(pwoName, newStatus, notes),
    onSuccess: () => {
      invalidatePersonalizationQueue(queryClient, tenant);
      // Also invalidate any open detail drawer
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "fulfillment" &&
          query.queryKey[1] === "pwo-detail",
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update status");
    },
  });
}

export function useUploadWorkPhoto() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<
    { file_url: string },
    Error,
    { pwoName: string; file: File }
  >({
    mutationFn: ({ pwoName, file }) =>
      fulfillmentApi.uploadWorkPhoto(pwoName, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "fulfillment" &&
          query.queryKey[1] === "pwo-detail",
      });
      invalidatePersonalizationQueue(queryClient, tenant);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to upload photo");
    },
  });
}

// ── Epic 06: Partial Shipments + Mixed Orders hooks ──────────

export function useOrderReadiness(salesOrder: string | null) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.orderReadiness(salesOrder || "", tenant),
    queryFn: () => fulfillmentApi.getOrderReadiness(salesOrder!),
    enabled: !!tenant && !!salesOrder,
    placeholderData: keepPreviousData,
  });
}

export function useShipmentHistory(salesOrder: string | null) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.shipmentHistory(salesOrder || "", tenant),
    queryFn: () => fulfillmentApi.getShipmentHistory(salesOrder!),
    enabled: !!tenant && !!salesOrder,
    placeholderData: keepPreviousData,
  });
}

export function usePartialShipmentSettings() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.partialShipmentSettings(tenant),
    queryFn: () => fulfillmentApi.getPartialShipmentSettings(),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

export function useUpdatePartialShipmentSettings() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean },
    Error,
    Partial<PartialShipmentSettings>
  >({
    mutationFn: (settings) =>
      fulfillmentApi.updatePartialShipmentSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.partialShipmentSettings(tenant),
      });
      // Also invalidate pending orders — policy change affects what's shown
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.pendingOrders(tenant),
      });
      queryClient.invalidateQueries({
        queryKey: fulfillmentKeys.batchSuggestions(tenant),
      });
      toast.success("Settings updated");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update settings");
    },
  });
}

export function usePersonalizationEnabled() {
  const { data: settings } = usePartialShipmentSettings();
  // Default to true for backward compat (existing tenants see it until they disable)
  return settings?.personalization_enabled ?? true;
}

// ── Epic 07: Manager Dashboard hooks ──────────────────────────

export function useDashboardMetrics(dateRange: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.dashboardMetrics(dateRange, tenant),
    queryFn: () => fulfillmentApi.getDashboardMetrics(dateRange),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useWorkerPerformance(dateRange: string) {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.workerPerformance(dateRange, tenant),
    queryFn: () => fulfillmentApi.getWorkerPerformance(dateRange),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useFulfillmentAlerts() {
  const tenant = useTenant();
  return useQuery({
    queryKey: fulfillmentKeys.fulfillmentAlerts(tenant),
    queryFn: () => fulfillmentApi.getFulfillmentAlerts(),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}
